import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { QRCodeSVG } from 'qrcode.react';
import { conferences, presentations, qr } from '../api';
import TimeSelect from '../components/TimeSelect';
import Navbar from '../components/Navbar';

const Conference = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [conference, setConference] = useState(null);
  const [presentationList, setPresentationList] = useState([]);
  const [qrCodes, setQrCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showPauseForm, setShowPauseForm] = useState(false);
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [uploadForm, setUploadForm] = useState({
    title: '', speaker_name: '', speaker_organization: '', event_date: '', start_time: '', end_time: '', file: null,
  });
  const [pauseForm, setPauseForm] = useState({
    title: '', description: '', event_date: '', start_time: '', end_time: '',
  });
  const [infoForm, setInfoForm] = useState({
    title: '', description: '', event_date: '', start_time: '', end_time: '',
  });

  useEffect(() => {
    conferences.getOne(id)
      .then((res) => { setConference(res.data); setPresentationList(res.data.presentations || []); })
      .catch(() => setError('Kunne ikke hente konferanse'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAllForms = () => {
    setShowUploadForm(false); setShowPauseForm(false); setShowInfoForm(false);
    setShowAddMenu(false); setEditingId(null);
  };

  const sortAndUpdate = async (items) => {
    const sorted = [...items].sort((a, b) => {
      const dateA = a.event_date ? a.event_date.split('T')[0] : '9999-12-31';
      const dateB = b.event_date ? b.event_date.split('T')[0] : '9999-12-31';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.start_time || '99:99';
      const timeB = b.start_time || '99:99';
      return timeA.localeCompare(timeB);
    });
    const updated = sorted.map((item, index) => ({ ...item, order_index: index }));
    setPresentationList(updated);
    try {
      await presentations.updateOrder(id, updated.map((p) => ({ id: p.id, order_index: p.order_index })));
    } catch { setError('Kunne ikke oppdatere rekkefølge'); }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(presentationList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const updated = items.map((item, index) => ({ ...item, order_index: index }));
    setPresentationList(updated);
    try {
      await presentations.updateOrder(id, updated.map((p) => ({ id: p.id, order_index: p.order_index })));
    } catch { setError('Kunne ikke oppdatere rekkefølge'); }
  };

  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    setUploadForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleEdit = (presentation) => {
    setEditingId(presentation.id);
    setEditForm({
      title: presentation.title || '',
      speaker_name: presentation.speaker_name || '',
      speaker_organization: presentation.speaker_organization || '',
      description: presentation.description || '',
      event_date: presentation.event_date ? presentation.event_date.split('T')[0] : '',
      start_time: presentation.start_time ? presentation.start_time.slice(0, 5) : '',
      end_time: presentation.end_time ? presentation.end_time.slice(0, 5) : '',
      type: presentation.type,
    });
    closeAllForms();
    setEditingId(presentation.id);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await presentations.update(id, editingId, editForm);
      const updated = presentationList.map((p) => p.id === editingId ? res.data : p);
      sortAndUpdate(updated);
      setEditingId(null);
    } catch (err) { setError(err.response?.data?.error || 'Kunne ikke oppdatere'); }
  };

  const handlePause = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await presentations.createPause(id, { ...pauseForm, order_index: presentationList.length, type: 'pause' });
      sortAndUpdate([...presentationList, res.data]);
      setPauseForm({ title: '', description: '', event_date: '', start_time: '', end_time: '' });
      setShowPauseForm(false);
    } catch (err) { setError(err.response?.data?.error || 'Kunne ikke opprette pause'); }
  };

  const handleInfo = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await presentations.createPause(id, { ...infoForm, order_index: presentationList.length, type: 'info' });
      sortAndUpdate([...presentationList, res.data]);
      setInfoForm({ title: '', description: '', event_date: '', start_time: '', end_time: '' });
      setShowInfoForm(false);
    } catch (err) { setError(err.response?.data?.error || 'Kunne ikke opprette infoblokk'); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('title', uploadForm.title);
    formData.append('speaker_name', uploadForm.speaker_name);
    formData.append('speaker_organization', uploadForm.speaker_organization);
    formData.append('start_time', uploadForm.start_time);
    formData.append('end_time', uploadForm.end_time);
    formData.append('event_date', uploadForm.event_date);
    formData.append('order_index', presentationList.length);
    try {
      const res = await presentations.upload(id, formData);
      sortAndUpdate([...presentationList, res.data]);
      setUploadForm({ title: '', speaker_name: '', speaker_organization: '', event_date: '', start_time: '', end_time: '', file: null });
      setShowUploadForm(false);
    } catch (err) { setError(err.response?.data?.error || 'Kunne ikke laste opp presentasjon'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (presentationId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette?')) return;
    try {
      await presentations.delete(id, presentationId);
      setPresentationList((prev) => prev.filter((p) => p.id !== presentationId));
    } catch { setError('Kunne ikke slette'); }
  };

  const handleGenerateQR = async () => {
    try {
      const res = await qr.generate(id);
      setQrCodes(res.data);
    } catch { setError('Kunne ikke generere QR-koder'); }
  };

  const getTypeStyle = (type) => {
    if (type === 'pause') return 'border-l-4 border-gold-500';
    if (type === 'info') return 'border-l-4 border-navy-400';
    return 'border-l-4 border-transparent';
  };

  const getTypeBadge = (type) => {
    if (type === 'pause') return <span className="text-xs bg-gold-500/10 text-gold-600 dark:text-gold-400 px-2 py-1 rounded-lg font-medium">Pause</span>;
    if (type === 'info') return <span className="text-xs bg-navy-100 dark:bg-navy-900/30 text-navy-600 dark:text-navy-300 px-2 py-1 rounded-lg font-medium">Info</span>;
    return null;
  };
  const groupByDate = (items) => {
  const groups = {};
  items.forEach((p) => {
    const date = p.event_date ? p.event_date.split('T')[0] : 'ingen-dato';
    if (!groups[date]) groups[date] = [];
    groups[date].push(p);
  });
  return groups;
};

const formatDayHeader = (dateStr) => {
  if (dateStr === 'ingen-dato') return 'Uten dato';
  return new Date(dateStr).toLocaleDateString('nb-NO', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
};
  const confMinDate = conference?.date?.split('T')[0];
  const confMaxDate = conference?.end_date ? conference.end_date.split('T')[0] : confMinDate;

  const inputClass = "input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100";

  if (loading) return <div className="flex items-center justify-center h-screen bg-cream-50 dark:bg-dark-200"><p className="text-navy-400">Laster...</p></div>;

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-dark-200">
      <Navbar title={conference?.title} backTo="/">
        <span className="text-cream-300 text-sm hidden md:block">
          {conference && new Date(conference.date).toLocaleDateString('nb-NO')}
          {conference?.end_date && ` - ${new Date(conference.end_date).toLocaleDateString('nb-NO')}`}
          {conference?.location && ` · ${conference.location}`}
        </span>
      </Navbar>

      <div className="max-w-4xl mx-auto p-6">
        {error && <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl mb-6 text-sm border border-red-200 dark:border-red-800">{error}</div>}

<div className="flex flex-wrap gap-3 mb-6">
  <button onClick={() => navigate(`/conference/${id}/present`)}
    className="flex items-center gap-2 bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-green-800 transition font-semibold shadow-sm text-base">
    ▶ Start presentasjon
  </button>
  <div className="flex gap-2 ml-auto">
    <button onClick={handleGenerateQR} className="btn-primary text-sm">
      QR-koder
    </button>
    <button onClick={() => navigate(`/conference/${id}/print`)} className="btn-secondary text-sm">
      🖨️ Skriv ut QR
    </button>
    <button onClick={() => navigate(`/conference/${id}/edit`)} className="btn-secondary text-sm">
      ⚙️ Rediger
    </button>
  </div>
</div>
        {qrCodes && (
          <div className="card dark:bg-dark-50 dark:border-dark-100 mb-6 grid grid-cols-2 gap-6">
            <div className="flex flex-col items-center">
              <h3 className="font-semibold text-navy-700 dark:text-cream-200 mb-3">Program</h3>
              <QRCodeSVG value={qrCodes.program.url} size={180} />
              <p className="text-xs text-navy-400 dark:text-cream-200 mt-2 text-center">Scan for å se dagens program</p>
            </div>
            <div className="flex flex-col items-center">
              <h3 className="font-semibold text-navy-700 dark:text-cream-200 mb-3">Last ned presentasjoner</h3>
              <QRCodeSVG value={qrCodes.download.url} size={180} />
              <p className="text-xs text-navy-400 dark:text-cream-200 mt-2 text-center">Scan for å laste ned presentasjoner</p>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-navy-800 dark:text-cream-100">Program</h2>
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowAddMenu(!showAddMenu)} className="btn-primary text-sm">
              + Legg til ▾
            </button>
            {showAddMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-dark-50 rounded-xl shadow-xl z-10 overflow-hidden border border-cream-200 dark:border-dark-100">
                <button onClick={() => { closeAllForms(); setShowUploadForm(true); }}
                  className="w-full text-left px-4 py-3 hover:bg-cream-50 dark:hover:bg-dark-100 text-sm text-navy-700 dark:text-cream-200 flex items-center gap-2 transition-colors">
                  🎤 Presentasjon
                </button>
                <button onClick={() => { closeAllForms(); setShowPauseForm(true); }}
                  className="w-full text-left px-4 py-3 hover:bg-cream-50 dark:hover:bg-dark-100 text-sm text-navy-700 dark:text-cream-200 flex items-center gap-2 transition-colors">
                  ☕ Pause
                </button>
                <button onClick={() => { closeAllForms(); setShowInfoForm(true); }}
                  className="w-full text-left px-4 py-3 hover:bg-cream-50 dark:hover:bg-dark-100 text-sm text-navy-700 dark:text-cream-200 flex items-center gap-2 transition-colors">
                  ℹ️ Info
                </button>
              </div>
            )}
          </div>
        </div>

        {showUploadForm && (
          <div className="card dark:bg-dark-50 dark:border-dark-100 mb-4">
            <h3 className="font-semibold text-navy-700 dark:text-cream-200 mb-4">🎤 Legg til presentasjon</h3>
            <form onSubmit={handleUpload}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Tittel *</label>
                  <input type="text" name="title" value={uploadForm.title} onChange={handleUploadChange} className={inputClass} required />
                </div>
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Foredragsholder *</label>
                  <input type="text" name="speaker_name" value={uploadForm.speaker_name} onChange={handleUploadChange} className={inputClass} required />
                </div>
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Organisasjon</label>
                  <input type="text" name="speaker_organization" value={uploadForm.speaker_organization} onChange={handleUploadChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Dato</label>
                  <input type="date" name="event_date" value={uploadForm.event_date} onChange={handleUploadChange}
                    min={confMinDate} max={confMaxDate} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Start</label>
                    <TimeSelect value={uploadForm.start_time} onChange={(val) => setUploadForm((prev) => ({ ...prev, start_time: val, end_time: '' }))} />
                  </div>
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Slutt</label>
                    <TimeSelect value={uploadForm.end_time} onChange={(val) => setUploadForm((prev) => ({ ...prev, end_time: val }))} minTime={uploadForm.start_time || undefined} />
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Fil * (PPT, PPTX, ODP, PDF)</label>
                <input type="file" name="file" onChange={handleUploadChange} accept=".ppt,.pptx,.odp,.pdf" className={inputClass} required />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={uploading} className="btn-primary disabled:opacity-50">
                  {uploading ? 'Laster opp...' : 'Last opp'}
                </button>
                <button type="button" onClick={() => setShowUploadForm(false)} className="btn-secondary">Avbryt</button>
              </div>
            </form>
          </div>
        )}

        {showPauseForm && (
          <div className="card dark:bg-dark-50 dark:border-dark-100 mb-4 border-l-4 border-gold-500">
            <h3 className="font-semibold text-navy-700 dark:text-cream-200 mb-4">☕ Legg til pause</h3>
            <form onSubmit={handlePause}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Tittel *</label>
                  <input type="text" value={pauseForm.title} onChange={(e) => setPauseForm((prev) => ({ ...prev, title: e.target.value }))}
                    className={inputClass} placeholder="Kaffepause" required />
                </div>
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Dato</label>
                  <input type="date" value={pauseForm.event_date} onChange={(e) => setPauseForm((prev) => ({ ...prev, event_date: e.target.value }))}
                    min={confMinDate} max={confMaxDate} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Start</label>
                    <TimeSelect value={pauseForm.start_time} onChange={(val) => setPauseForm((prev) => ({ ...prev, start_time: val, end_time: '' }))} />
                  </div>
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Slutt</label>
                    <TimeSelect value={pauseForm.end_time} onChange={(val) => setPauseForm((prev) => ({ ...prev, end_time: val }))} minTime={pauseForm.start_time || undefined} />
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Informasjon</label>
                <textarea value={pauseForm.description} onChange={(e) => setPauseForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={inputClass} placeholder="Lett fingermat serveres i foajeen" rows={3} />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="bg-gold-500 text-navy-900 px-4 py-2 rounded-lg hover:bg-gold-600 transition font-medium">Legg til</button>
                <button type="button" onClick={() => setShowPauseForm(false)} className="btn-secondary">Avbryt</button>
              </div>
            </form>
          </div>
        )}

        {showInfoForm && (
          <div className="card dark:bg-dark-50 dark:border-dark-100 mb-4 border-l-4 border-navy-400">
            <h3 className="font-semibold text-navy-700 dark:text-cream-200 mb-4">ℹ️ Legg til info</h3>
            <form onSubmit={handleInfo}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Tittel *</label>
                  <input type="text" value={infoForm.title} onChange={(e) => setInfoForm((prev) => ({ ...prev, title: e.target.value }))}
                    className={inputClass} placeholder="Åpning ved ordfører" required />
                </div>
                <div>
                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Dato</label>
                  <input type="date" value={infoForm.event_date} onChange={(e) => setInfoForm((prev) => ({ ...prev, event_date: e.target.value }))}
                    min={confMinDate} max={confMaxDate} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Start</label>
                    <TimeSelect value={infoForm.start_time} onChange={(val) => setInfoForm((prev) => ({ ...prev, start_time: val, end_time: '' }))} />
                  </div>
                  <div>
                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Slutt</label>
                    <TimeSelect value={infoForm.end_time} onChange={(val) => setInfoForm((prev) => ({ ...prev, end_time: val }))} minTime={infoForm.start_time || undefined} />
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Beskrivelse</label>
                <textarea value={infoForm.description} onChange={(e) => setInfoForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={inputClass} placeholder="Kulturelt innslag av..." rows={3} />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="bg-navy-600 text-white px-4 py-2 rounded-lg hover:bg-navy-700 transition font-medium">Legg til</button>
                <button type="button" onClick={() => setShowInfoForm(false)} className="btn-secondary">Avbryt</button>
              </div>
            </form>
          </div>
        )}
                    {presentationList.length === 0 ? (
  <div className="card dark:bg-dark-50 dark:border-dark-100 text-center py-12">
    <p className="text-4xl mb-3">📋</p>
    <p className="text-navy-600 dark:text-cream-200 font-medium">Ingen innhold ennå</p>
    <p className="text-navy-400 dark:text-cream-200 text-sm mt-1">Legg til presentasjoner, pauser og info</p>
  </div>
) : (
  <DragDropContext onDragEnd={handleDragEnd}>
    <Droppable droppableId="presentations">
      {(provided) => (
        <div {...provided.droppableProps} ref={provided.innerRef} className="grid gap-3">
          {Object.entries(groupByDate(presentationList)).map(([date, items]) => (
            <div key={date}>
              {Object.keys(groupByDate(presentationList)).length > 1 && (
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-navy-200 dark:bg-navy-800"></div>
                  <p className="text-navy-500 dark:text-navy-400 text-xs font-bold uppercase tracking-wide capitalize">
                    {formatDayHeader(date)}
                  </p>
                  <div className="flex-1 h-px bg-navy-200 dark:bg-navy-800"></div>
                </div>
              )}
              {items.map((presentation) => {
                const index = presentationList.findIndex(p => p.id === presentation.id);
                return (

                    <Draggable key={presentation.id} draggableId={String(presentation.id)} index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}
                          className={`card dark:bg-dark-50 dark:border-dark-100 ${getTypeStyle(presentation.type)} py-3`}>
                          {editingId === presentation.id ? (
                            <form onSubmit={handleUpdate} className="w-full">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Tittel *</label>
                                  <input type="text" value={editForm.title} onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))} className={inputClass} required />
                                </div>
                                {(editForm.type === 'presentation' || !editForm.type) && (
                                  <div>
                                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Foredragsholder</label>
                                    <input type="text" value={editForm.speaker_name} onChange={(e) => setEditForm((prev) => ({ ...prev, speaker_name: e.target.value }))} className={inputClass} />
                                  </div>
                                )}
                                {(editForm.type === 'presentation' || !editForm.type) && (
                                  <div>
                                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Organisasjon</label>
                                    <input type="text" value={editForm.speaker_organization} onChange={(e) => setEditForm((prev) => ({ ...prev, speaker_organization: e.target.value }))} className={inputClass} />
                                  </div>
                                )}
                                <div>
                                  <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Dato</label>
                                  <input type="date" value={editForm.event_date} onChange={(e) => setEditForm((prev) => ({ ...prev, event_date: e.target.value }))}
                                    min={confMinDate} max={confMaxDate} className={inputClass} />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Start</label>
                                    <TimeSelect value={editForm.start_time} onChange={(val) => setEditForm((prev) => ({ ...prev, start_time: val, end_time: '' }))} />
                                  </div>
                                  <div>
                                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Slutt</label>
                                    <TimeSelect value={editForm.end_time} onChange={(val) => setEditForm((prev) => ({ ...prev, end_time: val }))} minTime={editForm.start_time || undefined} />
                                  </div>
                                </div>
                                {(editForm.type === 'pause' || editForm.type === 'info') && (
                                  <div className="col-span-2">
                                    <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Beskrivelse</label>
                                    <textarea value={editForm.description} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))} className={inputClass} rows={2} />
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button type="submit" className="btn-primary text-sm">Lagre</button>
                                <button type="button" onClick={() => setEditingId(null)} className="btn-secondary text-sm">Avbryt</button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <span {...provided.dragHandleProps} className="text-navy-300 dark:text-navy-500 cursor-grab text-xl select-none">⠿</span>
                                <div>
                                  <p className="font-semibold text-navy-800 dark:text-cream-100">{presentation.title}</p>
                                  {presentation.type === 'pause' && (
                                    <p className="text-sm text-gold-600 dark:text-gold-400">
                                      Pause{presentation.start_time && ` · ${presentation.start_time.slice(0, 5)}`}{presentation.end_time && ` - ${presentation.end_time.slice(0, 5)}`}
                                    </p>
                                  )}
                                  {presentation.type === 'info' && (
                                    <p className="text-sm text-navy-500 dark:text-navy-300">
                                      Info{presentation.start_time && ` · ${presentation.start_time.slice(0, 5)}`}{presentation.end_time && ` - ${presentation.end_time.slice(0, 5)}`}
                                    </p>
                                  )}
                                  {(presentation.type === 'presentation' || !presentation.type) && (
                                    <p className="text-sm text-navy-500 dark:text-navy-300">
                                      {presentation.speaker_name}{presentation.speaker_organization && ` · ${presentation.speaker_organization}`}
                                      {presentation.start_time && ` · ${presentation.start_time.slice(0, 5)}`}{presentation.end_time && ` - ${presentation.end_time.slice(0, 5)}`}
                                    </p>
                                  )}
                                  {presentation.description && (
                                    <p className="text-xs text-navy-400 dark:text-navy-400 mt-0.5">{presentation.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 items-center">
                                {(presentation.type === 'presentation' || !presentation.type) && presentation.file_type && (
                                  <span className="text-xs bg-navy-100 dark:bg-navy-900/30 text-navy-600 dark:text-navy-300 px-2 py-1 rounded-lg uppercase font-medium">
                                    {presentation.file_type.replace('.', '')}
                                  </span>
                                )}
                                {getTypeBadge(presentation.type)}
                                <button onClick={() => handleEdit(presentation)} className="text-navy-500 hover:text-navy-700 dark:text-navy-300 dark:hover:text-cream-100 text-sm transition-colors">
                                  Rediger
                                </button>
                                <button onClick={() => handleDelete(presentation.id)} className="text-red-400 hover:text-red-600 text-sm transition-colors">
                                  Slett
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
               </Draggable>
                  );
                })}
              </div>
            ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  </DragDropContext>
)}
      </div>
    </div>
  );
};

export default Conference;
